import { gql, type TypedDocumentNode } from "@apollo/client";
import type {
  IActiveRewardPromotion,
  IMyRewardsSummary as ICoreMyRewardsSummary,
  IRedemption,
  IRewardCatalogItem,
  IRewardTier,
  IRewardTransaction,
} from "@ticketon/rewards-core";
import {
  CATALOG_ITEM_FIELDS,
  PAGE_FIELDS,
  PROMOTION_FIELDS,
  REDEMPTION_FIELDS,
  REWARD_BALANCE_FIELDS,
  TIER_FIELDS,
  TRANSACTION_FIELDS,
} from "./fragments";
import type { IConnection, IPageVariables } from "../pagination";

/* ----------------------------- Summary -------------------------------- */

/** GraphQL `MyRewardsSummary`. Re-export of the `@ticketon/rewards-core` model. */
export type IMyRewardsSummary = ICoreMyRewardsSummary;

export const GET_MY_REWARDS_SUMMARY: TypedDocumentNode<
  { myRewardsSummary: IMyRewardsSummary },
  Record<string, never>
> = gql`
  ${REWARD_BALANCE_FIELDS}
  ${CATALOG_ITEM_FIELDS}
  ${PROMOTION_FIELDS}
  query GetMyRewardsSummary {
    myRewardsSummary {
      id
      balance {
        ...RewardBalanceFields
      }
      promotions {
        ...RewardPromotionFields
      }
      recommendedRewards {
        ...RewardCatalogItemFields
      }
    }
  }
`;

/* --------------------------- Transactions ----------------------------- */

export interface ITransactionFilterInput {
  types?: string[];
  purchaseTypes?: string[];
  from?: string;
  to?: string;
  search?: string;
}

export const GET_MY_REWARD_TRANSACTIONS: TypedDocumentNode<
  { myRewardTransactions: IConnection<IRewardTransaction> },
  IPageVariables & { filter?: ITransactionFilterInput }
> = gql`
  ${TRANSACTION_FIELDS}
  query GetMyRewardTransactions(
    $pageNumber: Int!
    $pageSize: Int!
    $filter: RewardTransactionFilterInput
  ) {
    myRewardTransactions(pageNumber: $pageNumber, pageSize: $pageSize, filter: $filter) {
      items {
        ...RewardTransactionFields
      }
      ${PAGE_FIELDS}
    }
  }
`;

export const GET_MY_REWARD_TRANSACTION: TypedDocumentNode<
  { myRewardTransaction: IRewardTransaction | null },
  { id: string }
> = gql`
  ${TRANSACTION_FIELDS}
  query GetMyRewardTransaction($id: ID!) {
    myRewardTransaction(id: $id) {
      ...RewardTransactionFields
    }
  }
`;

/* ------------------------------ Catalog ------------------------------- */

export interface ICatalogFilterInput {
  search?: string;
  rewardTypes?: string[];
  minPoints?: number;
  maxPoints?: number;
  eligibleOnly?: boolean;
}

export const GET_MY_REWARDS_CATALOG: TypedDocumentNode<
  { myRewardsCatalog: IConnection<IRewardCatalogItem> },
  IPageVariables & { filter?: ICatalogFilterInput }
> = gql`
  ${CATALOG_ITEM_FIELDS}
  query GetMyRewardsCatalog(
    $pageNumber: Int!
    $pageSize: Int!
    $filter: RewardCatalogFilterInput
  ) {
    myRewardsCatalog(pageNumber: $pageNumber, pageSize: $pageSize, filter: $filter) {
      items {
        ...RewardCatalogItemFields
      }
      ${PAGE_FIELDS}
    }
  }
`;

export const GET_MY_REWARD_CATALOG_ITEM: TypedDocumentNode<
  { myRewardCatalogItem: IRewardCatalogItem | null },
  { id: string }
> = gql`
  ${CATALOG_ITEM_FIELDS}
  query GetMyRewardCatalogItem($id: ID!) {
    myRewardCatalogItem(id: $id) {
      ...RewardCatalogItemFields
    }
  }
`;

/* ---------------------------- Redemptions ----------------------------- */

export const GET_MY_REDEMPTIONS: TypedDocumentNode<
  { myRedemptions: IConnection<IRedemption> },
  IPageVariables
> = gql`
  ${REDEMPTION_FIELDS}
  query GetMyRedemptions($pageNumber: Int!, $pageSize: Int!) {
    myRedemptions(pageNumber: $pageNumber, pageSize: $pageSize) {
      items {
        ...RedemptionFields
      }
      ${PAGE_FIELDS}
    }
  }
`;

export const GET_MY_REDEMPTION: TypedDocumentNode<
  { myRedemption: IRedemption | null },
  { id: string }
> = gql`
  ${REDEMPTION_FIELDS}
  query GetMyRedemption($id: ID!) {
    myRedemption(id: $id) {
      ...RedemptionFields
    }
  }
`;

/* ------------------------------- Tiers -------------------------------- */

export const GET_MY_REWARD_TIERS: TypedDocumentNode<
  { myRewardTiers: IRewardTier[] },
  Record<string, never>
> = gql`
  ${TIER_FIELDS}
  query GetMyRewardTiers {
    myRewardTiers {
      ...RewardTierFields
    }
  }
`;

export const GET_MY_ACTIVE_REWARD_PROMOTIONS: TypedDocumentNode<
  { myActiveRewardPromotions: IActiveRewardPromotion[] },
  Record<string, never>
> = gql`
  ${PROMOTION_FIELDS}
  query GetMyActiveRewardPromotions {
    myActiveRewardPromotions {
      ...RewardPromotionFields
    }
  }
`;

/* ----------------------------- Mutations ------------------------------ */

/** `RedeemRewardInput`. Validate with `redeemRewardSchema` from `@ticketon/rewards-core`. */
export interface IRedeemRewardInput {
  catalogItemId: string;
  /** Defaults to 1 server-side. */
  quantity?: number;
  purchaseReference?: string;
}

export const REDEEM_REWARD: TypedDocumentNode<
  { redeemReward: IRedemption },
  { input: IRedeemRewardInput }
> = gql`
  ${REDEMPTION_FIELDS}
  mutation RedeemReward($input: RedeemRewardInput!) {
    redeemReward(input: $input) {
      ...RedemptionFields
    }
  }
`;

export const CANCEL_REWARD_REDEMPTION: TypedDocumentNode<
  { cancelRewardRedemption: IRedemption },
  { id: string; reason?: string }
> = gql`
  ${REDEMPTION_FIELDS}
  mutation CancelRewardRedemption($id: ID!, $reason: String) {
    cancelRewardRedemption(id: $id, reason: $reason) {
      ...RedemptionFields
    }
  }
`;
